import { useLayoutEffect, useRef, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useFilterStore } from '@/entities/discovery';
import { useSearchStore } from '@/entities/search';

import { decodeSearchParams } from '@/features/dataSearch/lib';

const INITIALIZATION_DELAY_MS = 100;

type UseSearchUrlSyncOptions = {
  onInitialMount?: (hasParams: boolean) => void;
};

type UseSearchUrlSyncReturn = {
  isReady: boolean;
  hasUrlParams: boolean;
};

export const useSearchUrlSync = ({ onInitialMount }: UseSearchUrlSyncOptions = {}): UseSearchUrlSyncReturn => {
  const [searchParams] = useSearchParams();
  const { setQuery, setFilters: setSearchFilters, setHasUrlParams, setInitialized } = useSearchStore();
  const { setFiltersAndUpdateOptions } = useFilterStore();
  const isInitialMount = useRef(true);
  const [isReady, setIsReady] = useState(false);
  const [hasUrlParams, setHasUrlParamsState] = useState(false);

  /** aggregations 로드를 기다리지 않고 URL을 먼저 반영해, 필터 없는 검색 요청이 먼저 나가지 않게 함 */
  useLayoutEffect(() => {
    const { query: urlQuery, filters: urlFilters } = decodeSearchParams(searchParams);
    const hasParams =
      Boolean(urlQuery) ||
      Object.values(urlFilters).some((filterArray) =>
        Array.isArray(filterArray) ? filterArray.length > 0 : Boolean(filterArray)
      );

    const applyUrlParams = () => {
      if (hasParams) {
        if (urlQuery) {
          setQuery(urlQuery);
        }
        setFiltersAndUpdateOptions(urlFilters);
        setSearchFilters(urlFilters);
      } else {
        useFilterStore.getState().clearAllFilters();
        setQuery('');
      }
    };

    if (isInitialMount.current) {
      setHasUrlParams(hasParams);
      setHasUrlParamsState(hasParams);
      applyUrlParams();

      if (hasParams) {
        setTimeout(() => {
          setInitialized(true);
        }, INITIALIZATION_DELAY_MS);
      }

      if (onInitialMount) {
        onInitialMount(hasParams);
      }

      isInitialMount.current = false;
      setIsReady(true);
      return;
    }

    setHasUrlParams(hasParams);
    setHasUrlParamsState(hasParams);
    applyUrlParams();
  }, [
    searchParams,
    setQuery,
    setFiltersAndUpdateOptions,
    setSearchFilters,
    setInitialized,
    setHasUrlParams,
    onInitialMount,
  ]);

  return { isReady, hasUrlParams };
};
